import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { Badge } from "@/shared/ui/badge";
import { elapsedSince } from "../lib/relativeTime";

export interface MediaInProgressCardProps {
  name: string;
  /** 右上角标(provider / 档位等);省略则不渲染。 */
  badge?: ReactNode;
  /**
   * 产物进度。**只有多产物的线(海报 n 张图)才传**;视频/分身是单产物,传了就是硬造进度。
   * done = 已出结果数(成功 + 失败),total = 请求数。
   */
  progress?: { done: number; total: number };
  /** 状态文案(排队中 / 生成中;1104 起排队单可为「排队中 · 前面 N 单 · 约 X 分钟」)。 */
  statusLabel: string;
  /** 状态补充说明(1104:本户上限卡住时的提示),挂在状态文案的 title 上,不另占一行。 */
  statusHint?: string | null;
  /** 任务创建时间 → 「已等待 N 分钟」。 */
  createdAt?: string | null;
  /**
   * 谁提交的这一单。
   *
   * ★2026-08-11 加(测试反馈 E-07,爱民 8·10):「在这里是不是应该体现一个,谁在做?
   *   未来客户公司肯定也有很多客服,这里不显示名字,有点不方便」。
   *   作品列表本来就显示创建人(「视觉舱 · 爱民」),唯独在途卡没有 —— 而在途恰恰是
   *   最需要认领的那一段:一屋子人同时在跑,谁也说不清哪条是自己的。
   * ★只有一个人的租户不必显示(传 null 即可),避免每张卡都挂一个同样的名字。
   */
  creatorName?: string | null;
  /**
   * 「来自哪张卡」标签(1006 复测:与完成卡同口径,调用方用 `workOriginLabel` 算好传入)。
   * 省略 / null 不渲染。
   */
  originLabel?: string | null;
  onClick: () => void;
}

/**
 * 进行中卡(2026-07-16 三线布局统一)。
 *
 * <p>三线的「进行中」节共用。海报多图 → 传 progress 显进度条;视频/分身单产物 → 不传,只显状态 + 已等待。
 *
 * <p>★<b>没有 ETA</b>:后端 `estimatedSecondsRemaining` 从建这个字段起就写死 null(mapper 里直接传 null,
 * 也没有任何估算模型),旧海报卡里那行「预计 N 秒」<b>一次都没渲染过</b>。与其留个永远不亮的字段,
 * 不如显示「已等待 N 分钟」—— 前端从 createdAt 就能算,不骗人。
 *
 * <p>★<b>进度条也曾是死的</b>:successCount/failedCount 在列表接口里恒 null → done 恒 0 → 进度条永远 0%。
 * 已在 api 侧修(PosterListCountsTest),这里才敢渲染它。
 */
export function MediaInProgressCard({
  name,
  badge,
  progress,
  statusLabel,
  statusHint,
  createdAt,
  creatorName,
  originLabel,
  onClick,
}: MediaInProgressCardProps) {
  const pct = progress ? Math.round((progress.done / Math.max(1, progress.total)) * 100) : null;
  const waited = elapsedSince(createdAt);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group fx-enter fx-hover-lift fx-press flex flex-col gap-3 rounded-2xl border bg-card p-4 text-left transition-colors hover:bg-accent/30"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="truncate text-sm font-semibold">{name}</h3>
        {(originLabel || badge) && (
          <div className="flex shrink-0 items-center gap-1.5">
            {originLabel && (
              <span
                className="max-w-[9rem] truncate rounded-full bg-muted px-1.5 py-0.5 text-2xs font-medium text-muted-foreground"
                title={originLabel}
              >
                {originLabel}
              </span>
            )}
            {badge}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-warning" />
        {progress ? (
          <span className="font-mono">
            {progress.done}/{progress.total} 已完成
          </span>
        ) : (
          <span className="min-w-0 truncate" title={statusHint ?? undefined}>
            {statusLabel}
          </span>
        )}
        {waited && <span className="ml-auto shrink-0 font-mono">已等待 {waited}</span>}
      </div>

      {/* 谁提交的(E-07)。与「已等待」分行:那一行是状态,这一行是归属,挤在一起会互相抢。 */}
      {creatorName && (
        <div className="-mt-1 truncate text-xs text-muted-foreground" title={creatorName}>
          {creatorName}
        </div>
      )}

      {/* 单产物线没有 n/m,不画一条永远填不满的假进度条 —— 那正是这次要清掉的东西。 */}
      {pct !== null && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-warning transition-all"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${name} 生成进度`}
          />
        </div>
      )}
    </button>
  );
}

/** 状态角标(进行中节共用):排队中 / 生成中。 */
export function InProgressStatusBadge({ label }: { label: string }) {
  return (
    <Badge variant="outline" className="shrink-0 text-2xs">
      {label}
    </Badge>
  );
}
